import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Send, Loader2, Trash2, Sparkles, X, Maximize2, Minimize2, GripVertical,
  TrendingUp, ShieldAlert, Activity, GitBranch, Ticket, HelpCircle, BookOpen, Bell, Wand2,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = {
  small: { w: 340, h: 460 },
  medium: { w: 400, h: 580 },
  large: { w: 520, h: 720 },
};

// Mapping icon name (from backend MODE_CATALOG) -> lucide component.
// Keeps the icon catalogue future-proof: backend just emits a string.
const MODE_ICON = {
  'trending-up': TrendingUp,
  'shield-alert': ShieldAlert,
  'activity': Activity,
  'git-branch': GitBranch,
  'send': Send,
  'ticket': Ticket,
  'help-circle': HelpCircle,
  'book-open': BookOpen,
  'bell': Bell,
};

// Simple monotonic message-id generator (per module). Stable across renders,
// strictly increasing — avoids using array indices as React keys.
let __aiMsgIdSeq = 0;
const mkMsg = (m) => ({ id: `m-${++__aiMsgIdSeq}-${Date.now()}`, ...m });

const AUTO_MODE = { key: null, label: 'Auto', icon: 'sparkles', description: 'Asistente segun tu rol' };

const AiAssistant = () => {
  const { getAuthHeaders } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [size, setSize] = useState('medium');
  const [pos, setPos] = useState({ x: null, y: null });
  const [dragging, setDragging] = useState(false);
  const [modes, setModes] = useState([]);      // server-filtered, RBAC-aware
  const [activeMode, setActiveMode] = useState(null); // null = auto/default
  const dragOffset = useRef({ x: 0, y: 0 });
  const panelRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);

  // Fetch RBAC-filtered modes when the widget first opens.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/ai/modes`, { headers: getAuthHeaders() });
        if (!cancelled) setModes(res.data?.modes || []);
      } catch (_) {
        if (!cancelled) setModes([]); // fail closed
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, getAuthHeaders]);

  useEffect(() => {
    if (isOpen && !sessionId) {
      setSessionId(`ai-${Date.now()}`);
      setMessages([mkMsg({ role: 'assistant', content: 'Hola, soy tu asistente AI. Elige un modo o escribe tu consulta.' })]);
    }
  }, [isOpen, sessionId]);

  const onMouseDown = useCallback((e) => {
    if (!panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragging(true);
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const x = e.clientX - dragOffset.current.x;
      const y = e.clientY - dragOffset.current.y;
      const maxX = window.innerWidth - (SIZES[size]?.w || 400);
      const maxY = window.innerHeight - (SIZES[size]?.h || 540);
      setPos({ x: Math.max(0, Math.min(x, maxX)), y: Math.max(0, Math.min(y, maxY)) });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging, size]);

  const onTouchStart = useCallback((e) => {
    if (!panelRef.current) return;
    const touch = e.touches[0];
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onTouchMove = (e) => {
      const touch = e.touches[0];
      const x = touch.clientX - dragOffset.current.x;
      const y = touch.clientY - dragOffset.current.y;
      const maxX = window.innerWidth - (SIZES[size]?.w || 400);
      const maxY = window.innerHeight - (SIZES[size]?.h || 540);
      setPos({ x: Math.max(0, Math.min(x, maxX)), y: Math.max(0, Math.min(y, maxY)) });
    };
    const onTouchEnd = () => setDragging(false);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    return () => { window.removeEventListener('touchmove', onTouchMove); window.removeEventListener('touchend', onTouchEnd); };
  }, [dragging, size]);

  const cycleSize = () => {
    const order = ['small', 'medium', 'large'];
    const next = order[(order.indexOf(size) + 1) % order.length];
    setSize(next);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, mkMsg({ role: 'user', content: msg })]);
    setLoading(true);
    try {
      const payload = { message: msg, session_id: sessionId };
      if (activeMode) payload.mode = activeMode;
      const res = await axios.post(`${API_URL}/ai/chat`, payload, { headers: getAuthHeaders() });
      setMessages(prev => [...prev, mkMsg({
        role: 'assistant',
        content: res.data.response,
        actions: res.data.actions_taken,
        model: res.data.model_used,
      })]);
      if (res.data.session_id) setSessionId(res.data.session_id);
    } catch (e) {
      setMessages(prev => [...prev, mkMsg({ role: 'assistant', content: 'Error al procesar. Intenta de nuevo.' })]);
    }
    setLoading(false);
  };

  const handleClear = async () => {
    try {
      await axios.delete(`${API_URL}/ai/chat/history?session_id=${sessionId}`, { headers: getAuthHeaders() });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[AiAssistant] clear history failed', err);
      }
    }
    setSessionId(`ai-${Date.now()}`);
    setMessages([mkMsg({ role: 'assistant', content: 'Conversacion reiniciada.' })]);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const pickMode = (key) => {
    setActiveMode(key);
    const meta = key ? modes.find(m => m.key === key) : AUTO_MODE;
    if (meta) {
      setMessages(prev => [...prev, mkMsg({
        role: 'assistant',
        content: `Modo activo: ${meta.label}. ${meta.description}`,
        system: true,
      })]);
    }
  };

  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-blue-700 hover:bg-blue-800 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-50"
        data-testid="ai-chat-toggle">
        <Sparkles className="w-6 h-6" />
      </button>
    );
  }

  const { w, h } = SIZES[size];
  const style = pos.x !== null ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : { right: 24, bottom: 24 };

  return (
    <Card ref={panelRef} className="fixed shadow-2xl border-0 rounded-2xl overflow-hidden z-50 flex flex-col"
      style={{ width: w, height: h, ...style, userSelect: dragging ? 'none' : 'auto' }}
      data-testid="ai-chat-panel">
      {/* Header - draggable */}
      <div className="bg-[#131722] px-3 py-2.5 flex items-center justify-between shrink-0 cursor-move"
        onMouseDown={onMouseDown} onTouchStart={onTouchStart}>
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-slate-500" />
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <p className="text-white text-xs font-semibold">Asistente AI</p>
            <p className="text-slate-500 text-[9px]">Aircom Connect</p>
          </div>
        </div>
        <div className="flex gap-0.5">
          <button onClick={cycleSize} className="p-1.5 text-slate-400 hover:text-white rounded" title={`Tamano: ${size}`} data-testid="ai-resize-btn">
            {size === 'large' ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button onClick={handleClear} className="p-1.5 text-slate-400 hover:text-white rounded" data-testid="ai-clear-btn"><Trash2 className="w-3.5 h-3.5" /></button>
          <button onClick={() => setIsOpen(false)} className="p-1.5 text-slate-400 hover:text-white rounded" data-testid="ai-close-btn"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* Mode selector (RBAC-filtered by server) */}
      <div className="bg-[#1c2030] border-b border-slate-800 shrink-0">
        <div className="flex gap-1 overflow-x-auto px-2 py-1.5 scrollbar-hide" data-testid="ai-mode-bar">
          <ModeChip
            label={AUTO_MODE.label}
            icon={<Wand2 className="w-3 h-3" />}
            active={activeMode === null}
            onClick={() => pickMode(null)}
            testId="ai-mode-auto"
          />
          {modes.map(m => {
            const Icon = MODE_ICON[m.icon] || Sparkles;
            return (
              <ModeChip
                key={m.key}
                label={m.label}
                icon={<Icon className="w-3 h-3" />}
                active={activeMode === m.key}
                onClick={() => pickMode(m.key)}
                title={m.description}
                testId={`ai-mode-${m.key}`}
              />
            );
          })}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-slate-50">
        {messages.map((msg, i) => (
          <div key={msg.id ?? `msg-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.system ? (
              <div className="mx-auto text-[10px] uppercase tracking-wider text-slate-400 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                {msg.content}
              </div>
            ) : (
              <div className={`max-w-[88%] p-2.5 rounded-2xl text-sm leading-relaxed ${
                msg.role === 'user' ? 'bg-blue-700 text-white rounded-br-md' : 'bg-white text-slate-700 shadow-sm border rounded-bl-md'
              }`} data-testid={`ai-msg-${i}`}>
                <div className="whitespace-pre-wrap">{msg.content}</div>
                {msg.actions?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200 space-y-1">
                    {msg.actions.map((a, j) => (
                      <div key={`${msg.id}-act-${j}`} className="text-xs bg-green-50 text-green-700 p-1.5 rounded border border-green-200">
                        Accion: {a.action}
                      </div>
                    ))}
                  </div>
                )}
                {msg.model && msg.role === 'assistant' && (
                  <div className="mt-1 text-[9px] text-slate-400 text-right">{msg.model === 'gemini' ? 'Gemini' : 'GPT-5.2'}</div>
                )}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white p-3 rounded-2xl shadow-sm border rounded-bl-md">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-2.5 bg-white border-t shrink-0">
        <div className="flex gap-2">
          <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
            placeholder="Escribe un comando..." className="flex-1 bg-slate-50 text-sm" disabled={loading} data-testid="ai-chat-input" />
          <Button onClick={handleSend} disabled={loading || !input.trim()} size="icon" className="bg-blue-700 hover:bg-blue-800 shrink-0" data-testid="ai-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[9px] text-slate-400 mt-1 text-center">Arrastra el header para mover</p>
      </div>
    </Card>
  );
};

const ModeChip = ({ label, icon, active, onClick, title, testId }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border ${
      active
        ? 'bg-blue-600 text-white border-blue-500'
        : 'bg-slate-800/40 text-slate-300 border-slate-700 hover:bg-slate-700/60 hover:text-white'
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
);

export default AiAssistant;
